/* global React */
/* ──────────────────────────────────────────────────────────────────────
   AboutPage — /about/ and /ar/about/  (2026-11 redesign, rev 2)
   hero → stats → story → leadership → founder bio → construction heritage
   → group structure (animated tree + ecosystem) → milestones (animated
   timeline) → vision → engineering-to-operations (data flow) → products →
   endorsements → regional footprint → selected work → how we work →
   future → company profile → FAQ (collapsed, last before the form).
   Styles: css/page-about.css (.dz-ab-* namespace).
   Copy: window.DZ_ABOUT (EN) / DZ_ABOUT_AR (aliased on /ar/).
   ────────────────────────────────────────────────────────────────────── */

const { useState: useAboutState, useEffect: useAboutEffect, useRef: useAboutRef } = React;

/* ── CountUp — animates 0 → target when scrolled into view ── */
function CountUp({ end, duration = 1800 }) {
  const [val, setVal] = useAboutState(0);
  const ref = useAboutRef(null);
  const started = useAboutRef(false);
  useAboutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const tick = (now) => {
          const t = Math.min(1, (now - t0) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          setVal(Math.round(end * eased));
          if (t < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{val}</span>;
}

/* ── Scroll-reveal hook — adds .is-in-view once visible ── */
function useAboutReveal(threshold) {
  const ref = useAboutRef(null);
  useAboutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let done = false;
    const reveal = () => { if (done) return; done = true; el.classList.add('is-in-view'); io.disconnect(); window.removeEventListener('scroll', onScroll); };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) reveal(); });
    }, { threshold: threshold || 0.12 });
    // Fallback: a fast scroll can skip the observer's threshold frame — reveal
    // whenever the section's top has entered the lower 85% of the viewport.
    let ticking = false;
    const check = () => { ticking = false; const r = el.getBoundingClientRect(); if (r.top < window.innerHeight * 0.85 && r.bottom > 0) reveal(); };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(check); } };
    io.observe(el);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { io.disconnect(); window.removeEventListener('scroll', onScroll); };
  }, [threshold]);
  return ref;
}

/* ── <picture> with AVIF / WebP / JPG fallback ── */
function AbPicture({ img, className, eager }) {
  if (!img) return null;
  return (
    <picture className={className}>
      {img.avif && <source srcSet={img.avif} type="image/avif" />}
      {img.webp && <source srcSet={img.webp} type="image/webp" />}
      <img
        src={img.src} alt={img.alt || ''}
        width={img.width} height={img.height}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        fetchpriority={eager ? 'high' : undefined}
      />
    </picture>
  );
}

/* ── Stroke glyphs ── */
function AbGlyph({ name, size = 22 }) {
  const I = {
    calendar: <g><rect x="3.5" y="5" width="17" height="15.5" rx="2"/><path d="M3.5 9.5h17M8 3v4M16 3v4"/></g>,
    layers:   <g><path d="M12 3.5 3.5 8 12 12.5 20.5 8 12 3.5z"/><path d="M3.5 12.5 12 17l8.5-4.5"/><path d="M3.5 16.5 12 21l8.5-4.5"/></g>,
    people:   <g><circle cx="9" cy="8.5" r="3.2"/><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6"/><circle cx="16.5" cy="9.5" r="2.4"/><path d="M15.5 14.2c2.9.3 5.5 2.8 5.5 5.8"/></g>,
    globe:    <g><circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17M12 3.5c2.6 2.6 3.9 5.4 3.9 8.5S14.6 17.9 12 20.5c-2.6-2.6-3.9-5.4-3.9-8.5S9.4 6.1 12 3.5z"/></g>,
    badge:    <g><path d="M12 3l7 3v5c0 4.4-2.9 7.4-7 9-4.1-1.6-7-4.6-7-9V6l7-3z"/><path d="M9.1 11.9l2.1 2.1 3.7-4.2"/></g>,
    flag:     <g><path d="M6 21V4"/><path d="M6 4h11l-2.5 3.5L17 11H6"/></g>,
    palm:     <g><path d="M12 21v-9"/><path d="M12 12c-1-3.5-4-5-7.5-4.5 2 1.5 4.5 2.5 7.5 4.5z"/><path d="M12 12c1-3.5 4-5 7.5-4.5-2 1.5-4.5 2.5-7.5 4.5z"/><path d="M12 12c-2.5-2-3-5.5-1.5-8 .5 2.5 1 5 1.5 8z"/><path d="M12 12c2.5-2 3-5.5 1.5-8-.5 2.5-1 5-1.5 8z"/></g>,
    crane:    <g><path d="M4 21h16"/><path d="M8 21V7l10-3v3H8"/><path d="M14 7v6"/><path d="M12.5 13h3"/><path d="M8 11h4"/></g>,
    cube:     <g><path d="M12 3.5 4 7.5v9l8 4 8-4v-9l-8-4z"/><path d="M4 7.5l8 4 8-4M12 11.5v9"/></g>,
    windows:  <g><rect x="4" y="9.5" width="8.5" height="8.5" rx="1.6"/><rect x="11.5" y="6" width="8.5" height="8.5" rx="1.6"/></g>,
    forward:  <g><path d="M5 6l6 6-6 6M12 6l6 6-6 6"/></g>,
    arrow:    <g><path d="M5 12h14M13 5l7 7-7 7"/></g>,
    down:     <g><path d="M12 5v14M5 12l7 7 7-7"/></g>,
    chev:     <g><path d="M6 9l6 6 6-6"/></g>,
    ext:      <g><path d="M14 4h6v6M20 4l-9 9"/><path d="M19 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5"/></g>,
    pin:      <g><path d="M12 21s-6.2-5.4-6.2-10.2a6.2 6.2 0 1 1 12.4 0C18.2 15.6 12 21 12 21z"/><circle cx="12" cy="10.6" r="2.2"/></g>,
    phone:    <g><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z"/></g>,
    linkedin: <g><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></g>,
    compass:  <g><circle cx="12" cy="5" r="2"/><path d="M12 7l-6 14M12 7l6 14"/><path d="M8.2 15.5c2.3 1.4 5.3 1.4 7.6 0"/></g>,
    box:      <g><path d="M3.5 8.5 12 4l8.5 4.5L12 13 3.5 8.5z"/><path d="M3.5 8.5V16L12 20.5 20.5 16V8.5"/><path d="M12 13v7.5"/><path d="M7.5 6.3l8.5 4.5"/></g>,
    twin:     <g><path d="M4 4h7v7H4zM13 13h7v7h-7z"/><path d="M11 7.5h2a2 2 0 0 1 2 2V11M13 16.5h-2a2 2 0 0 1-2-2V13"/></g>,
    chip:     <g><rect x="7" y="7" width="10" height="10" rx="2"/><path d="M10 10h4v4h-4zM12 3v4M12 17v4M3 12h4M17 12h4M7 3.5v3.5M17 3.5v3.5M7 17v3.5M17 17v3.5"/></g>,
    check:    <g><path d="M5 12.5l4.5 4.5L19 7.5"/></g>,
  };
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="dz-ab-glyph" data-g={name}>
      {I[name] || I.flag}
    </svg>
  );
}

/* ── Section heading ── */
function AbHead({ d, light, center, as = 'h2', id }) {
  const H = as;
  return (
    <div className={'dz-ab-head' + (center ? ' dz-ab-head--center' : '')}>
      <span className={'dz-eyebrow' + (light ? ' dz-eyebrow--on-light' : '')}>{d.eyebrow}</span>
      <H className="dz-ab-h2" id={id}>{d.titleLeading} <em>{d.titleAccent}</em></H>
      {d.lead && <p className="dz-ab-lead">{d.lead}</p>}
    </div>
  );
}

function AbCtas({ ctas, className }) {
  if (!ctas) return null;
  return (
    <div className={'dz-ab-ctas' + (className ? ' ' + className : '')}>
      {ctas.map((c, i) => (
        <a key={i} className={'dz-btn ' + (c.outline ? 'dz-ab-btn-outline' : 'dz-btn--aqua')} href={c.href}>
          {c.label}
          <AbGlyph name={c.down ? 'down' : 'arrow'} size={15} />
        </a>
      ))}
    </div>
  );
}

const extProps = (ext) => (ext ? { target: '_blank', rel: 'noopener' } : {});

/* ── Portrait: photo, or the profile's line-drawn mark ── */
function AbPortrait({ p }) {
  return (
    <figure className={'dz-ab-portrait' + (p.image ? '' : ' dz-ab-portrait--mark')}>
      {p.image
        ? <img src={p.image} alt={p.imageAlt || p.name} width="640" height="640" loading="lazy" decoding="async" />
        : p.mark
          ? <span className="dz-ab-portrait__ring"><img src={p.mark.src} alt={p.mark.alt} width={p.mark.width} height={p.mark.height} loading="lazy" decoding="async" /></span>
          : <span className="dz-ab-portrait__mono" aria-hidden="true">{p.initials}</span>}
      <span className="dz-ab-portrait__tick dz-ab-portrait__tick--tl" aria-hidden="true" />
      <span className="dz-ab-portrait__tick dz-ab-portrait__tick--br" aria-hidden="true" />
    </figure>
  );
}

/* ── Milestone (animated timeline node) ── */
function AbMilestone({ t, i, total }) {
  return (
    <li className={'dz-ab-tl__item' + (t.highlight ? ' dz-ab-tl__item--next' : '')} style={{ '--i': i, '--n': total }}>
      <span className="dz-ab-tl__node" aria-hidden="true"><AbGlyph name={t.glyph} size={18} /></span>
      <span className="dz-ab-tl__year">{t.year}</span>
      <div className="dz-ab-tl__card">
        <h3 className="dz-ab-tl__title">{t.title}</h3>
        <p className="dz-ab-tl__text">{t.text}</p>
      </div>
    </li>
  );
}

/* ── FAQ — collapsed behind a toggle (same chrome as the home FAQ toggle) ── */
/* Founder bio: paragraph 1 always visible; the rest unfolds on "Read more" (all text stays in the DOM). */
function FounderBody({ d }) {
  const [open, setOpen] = useAboutState(false);
  const [first, ...rest] = d.paragraphs;
  return (
    <div className="dz-ab-founder__body">
      <p>{first}</p>
      {rest.length > 0 && (
        <React.Fragment>
          <div id="dz-ab-founder-more" className={'dz-ab-founder__more' + (open ? ' is-open' : '')} aria-hidden={!open}>
            <div className="dz-ab-founder__more-inner">
              {rest.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
          <button type="button" className="dz-ab-founder__toggle" aria-expanded={open} aria-controls="dz-ab-founder-more" onClick={() => setOpen((v) => !v)}>
            <span>{open ? d.readLess : d.readMore}</span><AbGlyph name="chev" size={14} />
          </button>
        </React.Fragment>
      )}
    </div>
  );
}

function AboutFaq({ data }) {
  const [open, setOpen] = useAboutState(false);
  return (
    <section className="dz-section dz-faq dz-ab-faq" aria-label={data.sectionAriaLabel}>
      <div className="dz-section__inner">
        <span className="dz-eyebrow">{data.eyebrow}</span>
        <h2 className="dz-h2">{data.titleLeading} <em>{data.titleAccent}</em></h2>
        <p className="dz-lead">{data.lead}</p>
        <button
          type="button"
          className={'dz-faq__toggle' + (open ? ' is-open' : '')}
          aria-expanded={open}
          aria-controls="dz-about-faq-list"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? data.hideLabel : data.showLabel}
          <span className="dz-ab-faq__count">{data.items.length}</span>
          <span className="dz-faq__toggle-chev"><AbGlyph name="chev" size={16} /></span>
        </button>
        <div className="dz-faq__list" id="dz-about-faq-list" hidden={!open}>
          {data.items.map((it, i) => (
            <details key={i} className="dz-faq-item">
              <summary>{it.q}</summary>
              <div className="dz-faq-item__body">{it.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutPage() {
  const d = window.DZ_ABOUT;
  const offices = (window.DZ_FOOTER && window.DZ_FOOTER.offices) || [];

  const storyRef = useAboutReveal(0.15);
  const leadRef = useAboutReveal(0.12);
  const founderRef = useAboutReveal(0.12);
  const teamRef = useAboutReveal(0.1);
  const heritageRef = useAboutReveal(0.12);
  const structRef = useAboutReveal(0.18);
  const journeyRef = useAboutReveal(0.2);
  const visionRef = useAboutReveal(0.2);
  const handRef = useAboutReveal(0.2);
  const prodRef = useAboutReveal(0.12);
  const endorseRef = useAboutReveal(0.12);
  const footRef = useAboutReveal(0.12);
  const projRef = useAboutReveal(0.12);

  return (
    <main className="dz-about dz-ab" id="dz-main" aria-labelledby="about-title">

      {/* ── 1 · Hero ── */}
      <section className="dz-ab-hero">
        <div className="dz-ab-hero__media" aria-hidden="true">
          <AbPicture img={d.hero.image} eager />
          <span className="dz-ab-hero__wash" />
        </div>
        <div className="dz-ab-hero__inner">
          <nav className="dz-ab-crumb" aria-label="Breadcrumb">
            <a href={d.hero.breadcrumb.homeHref}>{d.hero.breadcrumb.homeLabel}</a>
            <span aria-hidden="true">›</span>
            <span aria-current="page">{d.hero.breadcrumb.pageLabel}</span>
          </nav>
          <span className="dz-eyebrow dz-ab-hero__eyebrow">{d.hero.eyebrow}</span>
          <h1 id="about-title" className="dz-ab-hero__title">
            {d.hero.titleLeading} <em>{d.hero.titleAccent}</em>
          </h1>
          <p className="dz-ab-hero__lead">{d.hero.lead}</p>
          <AbCtas ctas={d.hero.ctas} />
        </div>
        {d.hero.vision && (
          <a className="dz-ab-hero__vision" href={d.hero.vision.href}>
            <span className="dz-ab-hero__vision-mark" aria-hidden="true">2030</span>
            <span className="dz-ab-hero__vision-copy">
              <strong>{d.hero.vision.label}</strong>
              <span>{d.hero.vision.sub}</span>
            </span>
          </a>
        )}
      </section>

      {/* ── 2 · Stats strip ── */}
      <section className="dz-ab-stats" aria-label={d.stats.sectionAriaLabel}>
        <ul className="dz-ab-stats__list">
          {d.stats.items.map((s, i) => {
            const m = String(s.num).match(/^(\d+)(.*)$/);
            return (
              <li key={i} className="dz-ab-stat">
                <span className="dz-ab-stat__glyph"><AbGlyph name={s.glyph} /></span>
                <span className="dz-ab-stat__body">
                  <span className={'dz-ab-stat__num' + (s.text ? ' dz-ab-stat__num--text' : '')}>
                    {m && !s.text ? <><CountUp end={parseInt(m[1], 10)} duration={1500 + i * 150} />{m[2]}</> : s.num}
                  </span>
                  <span className="dz-ab-stat__label">{s.label}</span>
                </span>
              </li>
            );
          })}
        </ul>
      </section>

      {/* ── 3 · Our story ── */}
      <section className="dz-ab-story" ref={storyRef}>
        <div className="dz-ab-story__inner">
          <div className="dz-ab-story__copy">
            <AbHead d={d.story} light />
            {d.story.paragraphs.map((p, i) => <p key={i} className="dz-ab-p">{p}</p>)}
            <AbCtas ctas={[d.story.cta]} />
          </div>
          <div className="dz-ab-story__visual">
            <AbPicture img={d.story.image} className="dz-ab-story__pic" />
            <ol className="dz-ab-ladder" aria-label={d.story.ladder.prefix}>
              {d.story.ladder.steps.map((s, i) => (
                <li key={i} className="dz-ab-ladder__step" style={{ '--ld-i': i }}>
                  {i === 0 && <span className="dz-ab-ladder__prefix">{d.story.ladder.prefix}</span>}
                  <a href={s.href}><AbGlyph name="arrow" size={14} />{s.label}</a>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ── 4 · Leadership ── */}
      <section className="dz-ab-leaders" ref={leadRef} aria-label={d.leadership.sectionAriaLabel}>
        <div className="dz-ab-leaders__inner">
          <div className="dz-ab-leaders__head">
            <AbHead d={{ eyebrow: d.leadership.eyebrow, titleLeading: d.leadership.titleLeading, titleAccent: d.leadership.titleAccent }} light />
            <p className="dz-ab-leaders__intro">{d.leadership.lead}</p>
          </div>
          <div className="dz-ab-people">
            {d.leadership.people.map((p, i) => (
              <article key={p.id} className="dz-ab-person" style={{ '--pp-i': i }}>
                <AbPortrait p={p} />
                <div className="dz-ab-person__body">
                  <h3 className="dz-ab-person__name">{p.name}</h3>
                  {p.positions
                    ? <p className="dz-ab-person__role dz-ab-person__role--multi">{p.positions.map((x, k) => <span key={k} className="dz-ab-person__pos">{x.title}<span className="dz-ab-person__org"> · {x.org}</span></span>)}</p>
                    : <p className="dz-ab-person__role">{p.role}<span className="dz-ab-person__org">{p.org}</span></p>}
                  <p className="dz-ab-person__bio">{p.bio}</p>
                  {p.linkedin && (
                    <a className="dz-ab-person__in" href={p.linkedin.href} target="_blank" rel="noopener" aria-label={p.linkedin.label}>
                      <AbGlyph name="linkedin" size={15} /><span>LinkedIn</span>
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5 · Founder & Principal — detailed biography (preserved) ── */}
      <section className="dz-ab-founder" ref={founderRef} aria-label={d.founder.sectionAriaLabel}>
        <div className="dz-ab-founder__inner">
          <div className="dz-ab-founder__side">
            <figure className="dz-ab-founder__media">
              <img src={d.founder.image} alt={d.founder.imageAlt} width="640" height="640" loading="lazy" decoding="async" />
              <span className="dz-ab-founder__scan" aria-hidden="true" />
            </figure>
            <div className="dz-ab-founder__tb">
              <p className="dz-ab-founder__name">{d.founder.name}</p>
              <p className="dz-ab-founder__role">{d.founder.role}</p>
            </div>
          </div>
          <div className="dz-ab-founder__copy">
            <AbHead d={{ eyebrow: d.founder.eyebrow, titleLeading: d.founder.titleLeading, titleAccent: d.founder.titleAccent }} />
            <FounderBody d={d.founder} />
            <ol className="dz-ab-creds">
              {d.founder.chips.map((c, i) => (
                <li key={i} className="dz-ab-cred" style={{ '--fd-i': i }}>
                  <span className="dz-ab-cred__num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>{c}
                </li>
              ))}
            </ol>
            <a className="dz-ab-textlink dz-ab-textlink--on-dark" href={d.founder.linkedin.href} target="_blank" rel="noopener">
              {d.founder.linkedin.label}<AbGlyph name="arrow" size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* ── 5b · Our team — mirrors the Company Profile 2026 "Team & Leadership" page ── */}
      <section className="dz-ab-team" ref={teamRef} aria-label={d.team.sectionAriaLabel}>
        <div className="dz-ab-team__inner">
          <AbHead d={d.team} light />
          <ul className="dz-ab-team__grid">
            {d.team.people.map((p, i) => (
              <li key={i} className="dz-ab-tm" style={{ '--i': i }}>
                <figure className={'dz-ab-tm__photo' + (p.mark ? ' dz-ab-tm__photo--mark' : '')}>
                  {p.mark
                    ? <span className="dz-ab-tm__ring"><img src={p.mark} alt={p.name} width="600" height="573" loading="lazy" decoding="async" /></span>
                    : <picture>{p.webp && <source srcSet={p.webp} type="image/webp" />}<img src={p.image} alt={p.alt} width="640" height="640" loading="lazy" decoding="async" /></picture>}
                </figure>
                <div className="dz-ab-tm__body">
                  <h3 className="dz-ab-tm__name">{p.name}</h3>
                  <p className="dz-ab-tm__role">{p.role}</p>
                  <p className="dz-ab-tm__text">{p.text}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className="dz-ab-team__fn">
            <span className="dz-ab-team__fn-label">{d.team.functionsLabel}</span>
            <ul className="dz-ab-team__fn-list">
              {d.team.functions.map((f, i) => <li key={i} style={{ '--i': i }}>{f}</li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* ── 6 · Construction heritage — Chairman statement ── */}
      <section className="dz-ab-heritage" ref={heritageRef} aria-label={d.heritage.sectionAriaLabel}>
        <div className="dz-ab-heritage__inner">
          <div className="dz-ab-heritage__top">
            <AbHead d={d.heritage} light />
            <blockquote className="dz-ab-heritage__quote">
              <span className="dz-ab-heritage__mark" aria-hidden="true">
                <img src={d.leadership.people[0].mark.src} alt="" width="600" height="573" loading="lazy" decoding="async" />
              </span>
              <p>{d.heritage.statement}</p>
              <footer>{d.heritage.statementBy}</footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* ── 7 · Group structure — animated tree + ecosystem ── */}
      <section className="dz-ab-struct" ref={structRef} aria-label={d.structure.sectionAriaLabel}>
        <div className="dz-ab-struct__bg" aria-hidden="true"><AbPicture img={d.structure.image} /></div>
        <div className="dz-ab-struct__inner">
          <AbHead d={d.structure} />
          <div className="dz-ab-tree">
            <a className="dz-ab-tree__parent" href={d.structure.parent.href}>
              <img src={d.structure.parent.logo.src} alt={d.structure.parent.logo.alt} width={d.structure.parent.logo.width} height={d.structure.parent.logo.height} loading="lazy" decoding="async" />
              <span><strong>{d.structure.parent.name}</strong><small>{d.structure.parent.sub}</small></span>
            </a>
            <span className="dz-ab-tree__stem dz-ab-tree__stem--2" aria-hidden="true" />
            <span className="dz-ab-tree__bar" aria-hidden="true" />
            <ul className="dz-ab-tree__children">
              {d.structure.children.map((c, i) => (
                <li key={c.id} className={'dz-ab-tree__child dz-ab-tree__child--' + c.id} style={{ '--i': i }}>
                  <span className="dz-ab-tree__drop" aria-hidden="true" />
                  <a href={c.href} {...extProps(c.external)}>
                    <span className="dz-ab-tree__kind">{c.kind}</span>
                    <img src={c.logo.src} alt={c.logo.alt} width={c.logo.width} height={c.logo.height} loading="lazy" decoding="async" />
                    <strong>{c.name}</strong>
                    <small>{c.sub}</small>
                    {c.external && <AbGlyph name="ext" size={13} />}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div className="dz-ab-eco">
            <div className="dz-ab-eco__head">
              <h3 className="dz-ab-eco__title">{d.structure.ecosystemTitle}</h3>
              <p className="dz-ab-eco__lead">{d.structure.ecosystemLead}</p>
            </div>
            <ol className="dz-ab-reg">
              {d.structure.ecosystem.map((e, i) => (
                <li key={e.id} className="dz-ab-reg__row" style={{ '--i': i }}>
                  <span className="dz-ab-reg__idx" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                  <div className="dz-ab-reg__id">
                    <h4 className="dz-ab-reg__name">{e.name}</h4>
                    <span className="dz-ab-reg__sector">{e.sector}</span>
                    {e.href && (
                      <a className="dz-ab-reg__link" href={e.href} target="_blank" rel="noopener">
                        <span>{d.structure.ecosystemCta}</span><em>{e.host}</em><AbGlyph name="ext" size={12} />
                      </a>
                    )}
                  </div>
                  <div className="dz-ab-reg__fig"><strong>{e.fig}</strong><small>{e.figLabel}</small></div>
                  <p className="dz-ab-reg__desc">{e.desc}</p>
                  <ul className="dz-ab-reg__facts">
                    {e.facts.map((f, j) => <li key={j}><span>{f.k}</span><strong>{f.v}</strong></li>)}
                  </ul>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ── 8 · Journey — animated timeline ── */}
      <section className="dz-ab-journey" id="about-journey" ref={journeyRef} aria-label={d.journey.sectionAriaLabel}>
        <div className="dz-ab-journey__inner">
          <div className="dz-ab-journey__head">
            <AbHead d={{ eyebrow: d.journey.eyebrow, titleLeading: d.journey.titleLeading, titleAccent: d.journey.titleAccent }} light />
            <p className="dz-ab-journey__lead">{d.journey.lead}</p>
          </div>
          <div className="dz-ab-tl">
            <span className="dz-ab-tl__track" aria-hidden="true"><span className="dz-ab-tl__progress" /></span>
            <ol className="dz-ab-tl__list">
              {d.journey.items.map((t, i) => <AbMilestone key={i} t={t} i={i} total={d.journey.items.length} />)}
            </ol>
          </div>
        </div>
      </section>

      {/* ── 9 · Vision band ── */}
      <section className="dz-ab-vision" ref={visionRef} aria-label={d.vision.sectionAriaLabel}>
        <div className="dz-ab-vision__media" aria-hidden="true"><AbPicture img={d.vision.image} /></div>
        <div className="dz-ab-vision__inner">
          <span className="dz-ab-vision__label">{d.vision.label}</span>
          <p className="dz-ab-vision__text">{d.vision.text}</p>
        </div>
      </section>

      {/* ── 10 · Engineering to operations — the information spine ── */}
      <section className="dz-ab-hand" ref={handRef} aria-label={d.handover.sectionAriaLabel}>
        <div className="dz-ab-hand__inner">
          <AbHead d={d.handover} />
          <div className="dz-ab-flow" role="list" aria-label={d.handover.flowLabel}>
            <span className="dz-ab-flow__line" aria-hidden="true"><span className="dz-ab-flow__pulse" /></span>
            {d.handover.steps.map((s, i) => (
              <a key={i} className="dz-ab-flow__step" href={s.href} {...extProps(s.external)} role="listitem" style={{ '--i': i }}>
                <span className="dz-ab-flow__node" aria-hidden="true"><AbGlyph name={s.glyph} size={26} /></span>
                <span className="dz-ab-flow__num" aria-hidden="true">{s.num}</span>
                <span className="dz-ab-flow__tag">{s.tag}</span>
                <h3 className="dz-ab-flow__title">{s.title}{s.external && <AbGlyph name="ext" size={13} />}</h3>
                <p className="dz-ab-flow__text">{s.text}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── 11 · Products — two platforms in their own brand DNA ── */}
      <section className="dz-ab-prod" ref={prodRef} aria-label={d.products.sectionAriaLabel}>
        <div className="dz-ab-prod__inner">
          <AbHead d={d.products} light />
          <div className="dz-ab-prod__grid">
            {d.products.items.map((p, i) => (
              <article key={p.id} className={'dz-ab-pcard dz-ab-pcard--' + p.id} style={{ '--pc-i': i }}>
                <div className="dz-ab-pcard__copy">
                  <span className="dz-ab-pcard__tag">{p.tag}</span>
                  <img className="dz-ab-pcard__logo" src={p.logo.src} alt={p.logo.alt} width={p.logo.width} height={p.logo.height} loading="lazy" decoding="async" />
                  <p className="dz-ab-pcard__tagline">{p.tagline}</p>
                  <p className="dz-ab-pcard__desc">{p.desc}</p>
                  <ul className="dz-ab-pcard__modules" aria-label={p.name}>
                    {p.modules.map((m, k) => <li key={k} style={{ '--k': k }}>{m}</li>)}
                  </ul>
                  <ul className="dz-ab-pcard__stats">
                    {p.stats.map((s, k) => <li key={k}><strong>{s.num}</strong><span>{s.label}</span></li>)}
                  </ul>
                  <a className="dz-ab-pcard__link" href={p.href} {...extProps(p.external)}>
                    {p.linkLabel}<AbGlyph name={p.external ? 'ext' : 'arrow'} size={14} />
                  </a>
                </div>
                <figure className="dz-ab-pcard__shot">
                  <img src={p.shot.src} alt={p.shot.alt} width={p.shot.width} height={p.shot.height} loading="lazy" decoding="async" />
                </figure>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 12 · Endorsements ── */}
      <section className="dz-ab-endorse" ref={endorseRef} aria-label={d.endorsements.sectionAriaLabel}>
        <div className="dz-ab-endorse__inner">
          <AbHead d={d.endorsements} light center />
          <div className="dz-ab-endorse__grid">
            {d.endorsements.items.map((e, i) => {
              const Tag = e.href ? 'a' : 'article';
              const props = e.href ? { href: e.href, className: 'dz-ab-ecard dz-ab-ecard--link' } : { className: 'dz-ab-ecard' };
              return (
                <Tag key={i} {...props} style={{ '--i': i }}>
                  <div className="dz-ab-ecard__logo">
                    {e.logo
                      ? <img src={e.logo} alt={e.logoAlt} loading="lazy" decoding="async" />
                      : <span className="dz-ab-ecard__badge">{e.textBadge}</span>}
                  </div>
                  <h3 className="dz-ab-ecard__title">{e.title}{e.href && <span aria-hidden="true"> →</span>}</h3>
                  <p className="dz-ab-ecard__desc">{e.desc}</p>
                </Tag>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 13 · Selected work — image cards ── */}
      <section className="dz-ab-proj" id="about-work" ref={projRef} aria-label={d.projects.sectionAriaLabel}>
        <div className="dz-ab-proj__inner">
          <AbHead d={d.projects} light />
          <div className="dz-ab-proj__grid">
            {d.projects.items.map((p, i) => (
              <a key={i} className="dz-ab-pj" href={p.href} style={{ '--pj-i': i }}>
                <span className="dz-ab-pj__media">
                  <AbPicture img={p.image} />
                  <span className="dz-ab-pj__tag">{p.tag}</span>
                </span>
                <span className="dz-ab-pj__body">
                  <span className="dz-ab-pj__place">{p.place}</span>
                  <h3 className="dz-ab-pj__title">{p.title}<AbGlyph name="arrow" size={16} /></h3>
                  <p className="dz-ab-pj__desc">{p.desc}</p>
                </span>
              </a>
            ))}
          </div>
          <div className="dz-ab-ctas dz-ab-proj__more">
            <a className="dz-btn dz-btn--aqua" href={d.projects.allHref}>{d.projects.allLabel}<AbGlyph name="arrow" size={15} /></a>
          </div>
        </div>
      </section>

      {/* ── 14 · Company profile ── */}
      <section className="dz-ab-profile" aria-label={d.companyProfile.sectionAriaLabel}>
        <div className="dz-ab-profile__inner">
          <AbHead d={d.companyProfile} center />
          <div className="dz-ab-ctas dz-ab-ctas--center dz-ab-ctas--on-dark">
            <a className="dz-btn dz-btn--aqua" href={d.companyProfile.viewHref} target="_blank" rel="noopener">{d.companyProfile.viewLabel}</a>
            <a className="dz-btn dz-ab-btn-outline" href={d.companyProfile.pdfHref} download>{d.companyProfile.pdfLabel}</a>
          </div>
        </div>
      </section>

      {/* ── 15 · Regional footprint ── */}
      <section className="dz-ab-foot" ref={footRef} aria-label={d.footprint.sectionAriaLabel}>
        <div className="dz-ab-foot__inner">
          <AbHead d={d.footprint} light />
          <div className="dz-ab-foot__grid">
            {offices.map((o, i) => (
              <article key={o.id} className="dz-ab-office" style={{ '--of-i': i }}>
                <span className="dz-ab-office__tag">{o.hq ? d.footprint.hqTag : d.footprint.officeTag}</span>
                <h3 className="dz-ab-office__city">{o.city}</h3>
                <span className="dz-ab-office__country">{o.country}</span>
                <p className="dz-ab-office__role">{d.footprint.roles[o.id] || ''}</p>
                <p className="dz-ab-office__addr"><AbGlyph name="pin" size={15} />{o.addr}</p>
                <a className="dz-ab-office__phone" href={`tel:${o.tel}`} dir="ltr"><AbGlyph name="phone" size={15} />{o.phone}</a>
              </article>
            ))}
            {d.footprint.partner && (
              <article className="dz-ab-office dz-ab-office--partner" style={{ '--of-i': offices.length }}>
                <span className="dz-ab-office__tag">{d.footprint.partnerTag}</span>
                <h3 className="dz-ab-office__city">{d.footprint.partner.city}</h3>
                <span className="dz-ab-office__country">{d.footprint.partner.country}</span>
                <p className="dz-ab-office__role">{d.footprint.partner.role}</p>
                <img className="dz-ab-office__hc" src="/assets/about/hordecore-wordmark-white-2026.png" alt="HordeCore" width="900" height="86" loading="lazy" decoding="async" />
                <p className="dz-ab-office__addr">{d.footprint.partner.desc}</p>
                <a className="dz-ab-textlink dz-ab-textlink--on-dark" href={d.footprint.partner.href}>{d.footprint.partner.linkLabel}<AbGlyph name="arrow" size={14} /></a>
              </article>
            )}
          </div>
        </div>
      </section>

      {/* ── 16 · FAQ — collapsed, always last before the contact form ── */}
      <AboutFaq data={d.faq} />

    </main>
  );
}

window.AboutPage = AboutPage;
